import { useEffect, useRef, useState } from "react";
import { Spin } from "@app/ui";
import { useGoogleMaps } from "./loader.js";

export interface MapPinData {
  lat: number;
  lng: number;
  label?: string | undefined;
}

const DEFAULT_CENTER = { lat: 39.0, lng: 35.24 };
const DEFAULT_ZOOM = 5;
/** Single-pin ceiling — street-level zoom says nothing useful about a facility. */
const SINGLE_PIN_ZOOM = 9;

/**
 * Read-only pin map. With `chain` the pins are an ordered route: markers get
 * sequence numbers and a dashed geodesic polyline connects them (mirroring the
 * public passport map). Renders nothing at all when maps are disabled — parents
 * hide their surrounding card via useGoogleMaps() themselves if needed.
 */
export function PinsMap({
  pins,
  chain = false,
  height = 320,
}: {
  pins: MapPinData[];
  chain?: boolean;
  height?: number;
}) {
  const maps = useGoogleMaps();
  const boxRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const overlaysRef = useRef<(google.maps.Marker | google.maps.Polyline)[]>([]);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!maps || !boxRef.current) return;
    mapRef.current = new maps.Map(boxRef.current, {
      center: DEFAULT_CENTER,
      zoom: DEFAULT_ZOOM,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: true,
      gestureHandling: "cooperative",
    });
    setReady(true);
    return () => {
      for (const o of overlaysRef.current) o.setMap(null);
      overlaysRef.current = [];
      mapRef.current = null;
      setReady(false);
    };
  }, [maps]);

  // Re-derive all overlays whenever the pin set changes (cheap at these counts).
  const pinsKey = JSON.stringify(pins);
  useEffect(() => {
    const map = mapRef.current;
    if (!maps || !map || !ready) return;
    for (const o of overlaysRef.current) o.setMap(null);
    overlaysRef.current = [];
    if (pins.length === 0) return;

    const bounds = new maps.LatLngBounds();
    const path: google.maps.LatLngLiteral[] = [];
    pins.forEach((p, i) => {
      const pos = { lat: p.lat, lng: p.lng };
      path.push(pos);
      bounds.extend(pos);
      overlaysRef.current.push(
        new maps.Marker({
          map,
          position: pos,
          ...(p.label !== undefined ? { title: p.label } : {}),
          ...(chain ? { label: { text: String(i + 1), color: "#ffffff" } } : {}),
        }),
      );
    });
    if (chain && path.length > 1) {
      overlaysRef.current.push(
        new maps.Polyline({
          map,
          path,
          geodesic: true,
          strokeOpacity: 0,
          icons: [
            { icon: { path: "M 0,-1 0,1", strokeOpacity: 0.6, scale: 2.5 }, offset: "0", repeat: "14px" },
          ],
        }),
      );
    }
    if (pins.length === 1) {
      map.setCenter(path[0]!);
      map.setZoom(SINGLE_PIN_ZOOM);
    } else {
      map.fitBounds(bounds, 48);
    }
    // pinsKey stands in for the pins array identity.
  }, [maps, ready, chain, pinsKey]);

  if (maps === null) return null;

  return (
    <div className="map-canvas" style={{ height }}>
      <div ref={boxRef} className="map-fill" />
      {maps === undefined && (
        <div className="map-loading">
          <Spin />
        </div>
      )}
    </div>
  );
}
